import React, { Component } from "react";
import './App.css';

import { genId, defaultValues, selectShadowId } from "./shared/util";

import NavigationBar from './containers/NavigationBar/NavigationBar';
import Workspace from './containers/Workspace/Workspace';
import ControlContext from "./contexts/control-context";

import ChangeFillColorCommandObject from "./shared/commandObjects/ChangeFillColorCommandObject";
import ChangeCurrBorderColorCommandObject from "./shared/commandObjects/ChangeCurrBorderColorCommandObject";
import UpdateShapeCommandObject from "./shared/commandObjects/UpdateShapeCommandObject";
import ChangeCurrBorderWidthCommandObject from "./shared/commandObjects/ChangeCurrBorderWidthCommandObject";
import AddShapeCommandObject from "./shared/commandObjects/AddShapeCommandObject";
import DeleteShapeCommandObject from "./shared/commandObjects/DeleteShapeCommandObject";
import ReorderLayersCommandObject from "./shared/commandObjects/ReorderLayersCommandObject";
import AddFrameCommandObject from "./shared/commandObjects/AddFrameCommandObject";
import DeleteFrameCommandObject from "./shared/commandObjects/DeleteFrameCommandObject";
import AddTextCommandObject from "./shared/commandObjects/AddTextCommandObject";
import ReorderFramesCommandObject from "./shared/commandObjects/ReorderFramesCommandObject";
import InputMoveShapeCommandObject from "./shared/commandObjects/InputMoveCommandObject"


const init_id = genId()
const shapeNames = {
  'rect': 'Rectangle',
  'ellipse': 'Ellipse',
  'line': 'Line',
  'text': 'Text',
}

class App extends Component {
  state = {
    // controls
    currMode: defaultValues.mode,
    currBorderColor: defaultValues.borderColor,
    currBorderWidth: defaultValues.borderWidth,
    currFillColor: defaultValues.fillColor,
    currFontFamily: defaultValues.currFontFamily,
    currFontSize: defaultValues.currFontSize,

    // workspace
    shapes: [],
    shapesMap: {},
    selectedShapeId: undefined,

    currLayerName: undefined,

    frames: [init_id],
    framesMap: {
      [init_id]: {
        id: init_id,
        name: "frame 0",
        visible: true,
      }
    },
    selectedFrameId: init_id,

    currFrameInteraction: undefined,

    // handling undo/redo
    commandList: [],
    currCommand: -1,
  };

  constructor() {
    super();

    /*
     * pass this undoHandler into command object constructors:
     *  e.g. let cmdObj = new ChangeFillColorCommandObject(this.undoHandler);
     */
    this.undoHandler = {
      registerExecution: this.registerExecution,
      // TODO: fill this up with whatever you need for the command objects
      getState: () => {
        return this.state
      },
      updateShape: this.updateShape,
      updateShapeProps: this.updateShapeProps,
      selectShape: this.selectShape,
      reorderShapesArr: this.reorderShapesArr,
      reorderFramesArr: this.reorderFramesArr,
      inputMoveShape: this.inputMoveShape,
      updateFrame: this.updateFrame,
      selectFrame: this.selectFrame,
      findNextSelectedFrame: this.findNextSelectedFrame,
    };
  }

  /*
   * TODO:
   * add the commandObj to the commandList so
   * that is available for undoing.
   */
  registerExecution = (commandObject) => {
    let currCommand = this.state.currCommand
    let commandList = [...this.state.commandList]
    currCommand ++
    commandList.splice(currCommand)
    commandObject.frame = this.state.selectedFrameId
    commandList.push(commandObject)
    this.setState({currCommand: currCommand, commandList: commandList})
  };

  /*
   * TODO:
   * actually call the undo method of the command at
   * the current position in the undo stack
   */
  undo = () => {
    console.log("undo");
    let cmd = this.state.commandList[this.state.currCommand];
    if (cmd) {
      this.setState({currCommand: this.state.currCommand-1, selectedFrameId: cmd.frame});
      cmd.undo();
    }
  };

  /*
   * TODO:
   * actually call the redo method of the command at
   * the current position in the undo stack.
   */
  redo = () => {
    console.log("redo");
    let cmd = this.state.commandList[this.state.currCommand+1];
    if (cmd) {
      this.setState({currCommand: this.state.currCommand+1, selectedFrameId: cmd.frame});
      cmd.redo();
    }
  };

  addFrame = () => {
    let frames = [ ...this.state.frames ]
    let framesMap = { ...this.state.framesMap }
    const id = genId()
    framesMap[id] = {
      id,
      name: `frame ${frames.length}`,
      visible: true,
    }
    frames.push(id)
    this.setState({ frames, framesMap, selectedFrameId: id }, () => {
      let cmdObj = new AddFrameCommandObject(this.undoHandler, id);
      cmdObj.execute()
    })
  }

  changeFrameName = (id, newFrameName) => {
    let framesMap = { ...this.state.framesMap }
    framesMap[id] = {
      id, 
      name: newFrameName,
      visible: true,
    }
    console.log(framesMap)
    this.setState({ framesMap })
  }

  updateFrame = (id, data, select=true) => {
    let framesMap = { ...this.state.framesMap };
    let frame = framesMap[id];
    framesMap[id] = { ...frame, ...data };
    this.setState({ framesMap }, () => {
      if (select) {
        this.selectFrame(id)
      }
    });
  };

  selectFrame = (id) => {
    this.selectShape(undefined)
    this.setState({ selectedFrameId: id })
  }

  findNextSelectedFrame = (selectedId) => {
    const {
      frames,
      framesMap,
    } = this.state

    let frame = frames.filter((id, i) => i < frames.indexOf(selectedId) && framesMap[id].visible).at(-1)
    if (!frame) frame = frames.filter((id, i) => i > frames.indexOf(selectedId) && framesMap[id].visible).at(0)
    return frame
  }

  deleteSelectedFrame = () => {
    if (this.state.selectedFrameId) {
      let cmdObj = new DeleteFrameCommandObject(this.undoHandler)
      cmdObj.execute()
    }
  }

  addFrameInteraction = (frameInteraction) => {
    this.setState({ currFrameInteraction: frameInteraction });
    this.updateShapeProps(this.state.selectedShapeId, {frameInteraction})
  }

  // add the shapeId to the array, and the shape itself to the map
  addShape = (shapeData) => {
    let shapes = [...this.state.shapes];
    let shapesMap = { ...this.state.shapesMap };
    const id = genId();
    shapesMap[id] = {
      ...shapeData,
      id,
      frame: this.state.selectedFrameId,
      name: `${shapeNames[shapeData.type] ? shapeNames[shapeData.type] : 'Object'} ${shapes.filter((id) => shapesMap[id].type === shapeData.type).length}`
    };
    shapes.push(id);
    this.setState({ shapes, shapesMap, selectedShapeId: id }, () => {
      let cmdObj = new AddShapeCommandObject(this.undoHandler, shapesMap[id]);
      cmdObj.execute()
    });
    return id;
  };

  // get the shape by its id, and update its properties
  updateShapeProps = (shapeId, newData, select=true) => {
    let shapesMap = { ...this.state.shapesMap };
    let targetShape = shapesMap[shapeId];
    shapesMap[shapeId] = { ...targetShape, ...newData };
    this.setState({ shapesMap }, () => {
      if (select && this.state.currMode === "select") {
        this.selectShape(shapeId)
      }
    });
  };

  updateShape = (data, finished=false) => {
    if (this.state.selectedShapeId) {
      if (!finished) {
        this.updateShapeProps(this.state.selectedShapeId, data);
      } else {
        let obj = new UpdateShapeCommandObject(this.undoHandler, data);
        obj.execute();
      }
    }
  };

  inputMoveShape = (oldData, newData) => {
    if (this.state.selectedShapeId) {
      this.updateShapeProps(this.state.selectedShapeId, newData);
      console.log("update", newData, oldData);
      let obj = new InputMoveShapeCommandObject(this.undoHandler, oldData, newData);
      obj.execute();
    }
  }

  selectShape = (id) => {
    let selectedShapeId = this.state.selectedShapeId

    if (document.getElementById(selectedShapeId)) document.getElementById(selectedShapeId).classList.remove('selected')
    if (document.getElementById(id)) document.getElementById(id).classList.add('selected')

    this.setState({ selectedShapeId: id });
    if (id && !(this.state.currMode === "text")) {
      const { borderColor, borderWidth, fillColor } = this.state.shapesMap[
        this.state.shapes.filter((shapeId) => shapeId === id)[0]
      ];
      this.setState({
        currBorderColor: borderColor,
        currBorderWidth: borderWidth,
        currFillColor: fillColor,
      });
    }
  };

  reorderShapesArr = (from, to) => {
    let elem = this.state.shapes[from]
    let shapes = [...this.state.shapes]
    shapes.splice(from, 1)
    shapes.splice(to, 0, elem)
    this.setState({shapes: shapes})
  }

  reorderLayers = (from, to, finished=false) => {
    if (!finished) {
      this.reorderShapesArr(from, to)
    } else {
      if (from === to) return
      let obj = new ReorderLayersCommandObject(this.undoHandler, {oldData: from, newData: to})
      obj.execute()
    }
  }

  reorderFramesArr = (from, to) => {
    let elem = this.state.frames[from]
    let frames = [...this.state.frames]
    frames.splice(from, 1)
    frames.splice(to, 0, elem)
    this.setState({frames: frames})
  }

  reorderFrames = (from, to, finished=false) => {
    if (!finished) {
      this.reorderFramesArr(from, to)
    } else {
      if (from === to) return
      let obj = new ReorderFramesCommandObject(this.undoHandler, {oldData: from, newData: to})
      obj.execute()
    }
  }

  // deleting a shape sets its visibility to false, rather than removing it
  deleteSelectedShape = () => {
    let cmdObj = new DeleteShapeCommandObject(this.undoHandler)
    cmdObj.execute()
  };

  changeCurrMode = (mode) => {
    if (mode === "line") {
      this.setState({
        currMode: mode,
        currBorderColor: defaultValues.borderColor,
      });
    } else {
      this.setState({ currMode: mode });
    }
  };

  changeCurrBorderColor = (borderColor) => {
    this.setState({ currBorderColor: borderColor }, () => {
      let cmdObj = new ChangeCurrBorderColorCommandObject(this.undoHandler)
      cmdObj.execute()
    });
  };

  changeCurrBorderWidth = (borderWidth, finished=false) => {
    if (!finished) {
      this.setState({ currBorderWidth: borderWidth });
    }
    if (borderWidth && this.state.selectedShapeId) {
      if (finished) {
        let cmdObj = new ChangeCurrBorderWidthCommandObject(this.undoHandler, {borderWidth});
        cmdObj.execute();
      } else {
        this.updateShapeProps(this.state.selectedShapeId, { borderWidth });
      }
    }
  };

  changeCurrFillColor = (fillColor) => {
    this.setState({ currFillColor: fillColor }, () => {
      let cmdObj = new ChangeFillColorCommandObject(this.undoHandler)
      cmdObj.execute()
    });
  };

  addText = (shapeId) => {
    let cmdObj = new AddTextCommandObject(this.undoHandler, shapeId)
    cmdObj.execute();
  };

  changeCurrFontSize = (fontSize) => {
    this.setState({ currFontSize: fontSize });
    this.updateShapeProps(this.state.selectedShapeId, { fontSize })
  }

  changeCurrFontFamily = (fontFamily) => {
    this.setState({ currFontFamily: fontFamily });
    this.updateShapeProps(this.state.selectedShapeId, { fontFamily })
  }

  render() {
    const {
      currMode,
      currBorderColor,
      currBorderWidth,
      currFillColor,
      currFontFamily,
      currFontSize,
      shapes,
      shapesMap,
      selectedShapeId,
      currCommand,
      commandList,
      frames,
      framesMap,
      selectedFrameId,
      currFrameInteraction
    } = this.state;


  return (
    <React.Fragment>
      <ControlContext.Provider
          value={{
            currMode,
            changeCurrMode: this.changeCurrMode,
            currBorderColor,
            changeCurrBorderColor: this.changeCurrBorderColor,
            currBorderWidth,
            changeCurrBorderWidth: this.changeCurrBorderWidth,
            currFillColor,
            changeCurrFillColor: this.changeCurrFillColor,
            currFontFamily,
            changeCurrFontFamily: this.changeCurrFontFamily,
            currFontSize,
            changeCurrFontSize: this.changeCurrFontSize,

            shapes,
            shapesMap,
            addShape: this.addShape,
            updateShapeProps: this.updateShapeProps,
            updateShape: this.updateShape,
            reorderLayers: this.reorderLayers,
            inputMoveShape: this.inputMoveShape,
            selectedShapeId,
            selectShape: this.selectShape,
            deleteSelectedShape: this.deleteSelectedShape,
            addFrameInteraction: this.addFrameInteraction,
            currFrameInteraction,
            changeFrameName: this.changeFrameName,

            frames,
            framesMap,
            selectedFrameId,
            addFrame: this.addFrame,
            reorderFrames: this.reorderFrames,
            deleteSelectedFrame: this.deleteSelectedFrame,
            selectFrame: this.selectFrame,
            addText: this.addText,

            undo: this.undo,
            redo: this.redo,
            undoable: this.state.currCommand >= 0 && this.state.commandList.length > 0,
            redoable: this.state.currCommand < this.state.commandList.length - 1 && this.state.commandList.length > 0,
            currCommand,
            commandList,
          }}
        >
      <NavigationBar />
      <Workspace />
      </ControlContext.Provider>
    </React.Fragment>
  );
  }
}

export default App;
